import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import PropTypes from 'prop-types';
import { useAuth, getNamespace } from '@bb/session-manager';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import {
  Badge,
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';

import Logo from '../../assets/images/logo-icon-white.png';
import { LOG_LOGOUT_ENDPOINT, USERGUIDE_ENDPOINT } from '../../endpoints.config';
import {
  ACCOUNT_ADMIN_PERMISSION,
  BLACKBOILER_ADMIN_PERMISSION,
} from '../../groups.config';
import { setErrMsg, setSuccessMsg } from '../../redux/actions/snackbarActions';
import {
  ChangeTenant,
  GetCurrentTenant,
  GetTenants,
} from '../../requests/tenantManagement';
import { ACCOUNT_ROUTE, TENANTS_NEW_ROUTE } from '../../routes.config';
import { Flex } from '../../components';
import Colors from '../../styles/Colors';
import { LabeledSelect } from '../LabeledSelect';

import { openJobs, startCheckingJobs } from '../../redux/actions/asyncJobActions';
import AsyncJobsDialog from '../AsyncJobs/AsyncJobsDialog';

const TRUNCATED_LINKS = [
  'Home',
  'Contracts',
  'Users',
  'Clause Library',
  'Playbook Builder',
  'PoA',
  'Reporting',
];

const MINI_LINKS = ['Home', 'Contracts', 'Users', 'PoA'];

const AppBarFull = ({ navList, companyName = '', userName, pathname }) => {
  const { config, setConfig, user, bbAxios, signOut } = useAuth();

  const { jobs } = useSelector((state) => state.AsyncJob);

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const setSuccess = (value) => dispatch(setSuccessMsg(value));
  const setErr = (value) => dispatch(setErrMsg(value));

  const [anchorMenu, setAnchorMenu] = useState(null);
  const [anchorProfile, setAnchorProfile] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [allTenants, setAllTenants] = useState([]);
  const [isChangingTenant, setIsChangingTenant] = useState(false);

  const jobsAnchorRef = useRef();

  const isTruncated = useMediaQuery('(max-width:1444px)');
  const isMini = useMediaQuery('(max-width:1150px)');
  const isHidden = useMediaQuery('(max-width:800px)');

  useEffect(() => {
    const asyncEffect = async () => {
      await checkJobs();
      // Only load tenant data if multi-tenant management is enabled and user has permission
      if (
        config?.useTenantManagement &&
        user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION)
      ) {
        await loadTenantData();
      }
    };
    asyncEffect().catch((err) => console.error(err));
  }, [config, user]);

  const showTenantSelect = useMemo(
    () => !!tenant && !!Object.keys(tenant).length && allTenants.length > 0,
    [tenant, allTenants]
  );

  const loadTenantData = async () => {
    try {
      const [currentTenant, tenants] = await Promise.all([
        GetCurrentTenant(),
        GetTenants(),
      ]);
      setTenant(currentTenant);
      setAllTenants(tenants || []);
    } catch (error) {
      console.error('Failed to load tenant data:', error);
    }
  };

  const checkJobs = async () => {
    dispatch(startCheckingJobs(dispatch, { disableRefresh: true }));
  };

  const handleMenuClick = (event) => {
    setAnchorMenu(anchorMenu ? null : event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorMenu(null);
  };

  const handleProfileClick = (event) => {
    setAnchorProfile(anchorMenu ? null : event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorProfile(null);
  };

  const handleLogout = () => {
    setAnchorProfile(null);

    // Log the logout event (fire-and-forget, don't wait for response)
    // This prevents the axios interceptor from interfering with the logout flow
    bbAxios
      .get(LOG_LOGOUT_ENDPOINT)
      .catch((err) => console.error('Failed to log the user logout with error:', err));

    // Immediately sign out from the auth provider
    // This will redirect the user to the Cognito logout endpoint
    signOut();
  };

  const isActivePath = (path) => {
    const [, , thirdInPathname] = pathname.split('/');
    const [, , thirdInPath] = path.split('/');
    return thirdInPathname === thirdInPath;
  };

  const handleHelpIconClick = (event) => {
    event.preventDefault();
    bbAxios.get(USERGUIDE_ENDPOINT, { responseType: 'blob' }).then((resp) => {
      // Build a URL from the file
      const fileURL = URL.createObjectURL(resp.data);
      // Open the URL on new Window
      window.open(fileURL);
    });
  };

  const handleSetTenant = async (newTenantId) => {
    if (newTenantId === 'create') {
      navigate({ to: TENANTS_NEW_ROUTE });
    } else {
      try {
        setIsChangingTenant(true);
        // Get the socket ID from the live /global/event namespace so the backend
        // can exclude THIS tab from the "tenant changed" notification.
        const globalSocket = getNamespace('/global/event');
        await ChangeTenant(newTenantId, globalSocket?.id ?? null);

        // Fetch fresh config with new tenant permissions using V2 API
        const response = await bbAxios.get('/api/v2/config');
        if (response.data) {
          setConfig(response.data);
          setSuccess('Tenant changed successfully');
          navigate({ to: '/bbweb/home' });
        } else {
          // Fallback to reload if fetch fails
          setSuccess('Tenant changed');
          window.location.reload(true);
        }
      } catch (e) {
        console.error('Failed to change tenant:', e);
        const errorMsg =
          e.response?.data?.message || e.message || 'Failed to change tenant';
        setErr(errorMsg);

        // If it's a 401, the user might need to reload to get proper tenant context
        if (e.response?.status === 401) {
          setTimeout(() => {
            window.location.reload(true);
          }, 2000);
        }
      } finally {
        setIsChangingTenant(false);
      }
    }
  };

  const renderLinks = useMemo(
    () =>
      navList
        .filter(({ label }) => {
          if (isTruncated) {
            if (isHidden) {
              return false;
            }
            if (isMini) {
              return MINI_LINKS.includes(label);
            }
            return TRUNCATED_LINKS.includes(label);
          }
          return true;
        })
        .map(({ label, path, role }) =>
          role ? (
            <Link key={path} component={RouterLink} to={path} underline="none">
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '1.5rem',
                  letterSpacing: 0,
                  fontWeight: isActivePath(path) ? 700 : 400,
                  fontStyle: 'normal',
                  borderRadius: 0,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            </Link>
          ) : null
        ),
    [navList, isTruncated, isMini, isHidden]
  );

  const renderMenuItems = useMemo(
    () =>
      navList
        .filter(({ label, role }) => {
          // Only include items the user has permission for
          if (!role) return false;
          if (isTruncated) {
            if (isHidden) {
              return true;
            }
            if (isMini) {
              return !MINI_LINKS.includes(label);
            }
            return !TRUNCATED_LINKS.includes(label);
          }
          return false;
        })
        .map(({ label, path }) => (
          <Link
            key={label}
            component={RouterLink}
            to={path}
            underline="none"
            onClick={handleMenuClose}
          >
            <MenuItem key={label} sx={{ color: '#000' }}>
              {label}
            </MenuItem>
          </Link>
        )),
    [navList, handleMenuClose, isTruncated, isMini, isHidden]
  );

  // Only show hamburger menu if there are actually items to display
  const hasMenuItems = renderMenuItems.length > 0;

  return (
    <>
      <Flex.H
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Flex.H sx={{ alignItems: 'center', gap: '20px' }}>
          <Link component={RouterLink} to="/" underline="hover">
            <img
              src={Logo}
              alt="blackboiler logo"
              style={{
                marginBottom: '3px',
                display: 'block',
                width: '30px',
                height: '30px',
                objectFit: 'contain',
              }}
            />
          </Link>

          {renderLinks}

          {isTruncated && hasMenuItems && (
            <IconButton
              aria-controls="appbar-menu"
              aria-haspopup="true"
              onClick={handleMenuClick}
              color="inherit"
              size="large"
              sx={{ marginLeft: '-12px' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Flex.H>

        <Flex.H sx={{ alignItems: 'center', gap: '8px' }}>
          {showTenantSelect && allTenants.length > 1 ? (
            <Box>
              <LabeledSelect
                title=""
                value={tenant._id}
                setValue={(evt) => handleSetTenant(evt)}
                items={allTenants}
                keyField="_id"
                valueField="displayName"
                disabled={isChangingTenant}
                styleProps={{
                  fontWeight: 'bold',
                  color: Colors.trueWhite,
                  backgroundColor: Colors.black,
                  opacity: isChangingTenant ? 0.6 : 1,
                  cursor: isChangingTenant ? 'not-allowed' : 'pointer',
                }}
                extraMenuItems={[
                  !!config?.useTenantManagement &&
                    user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION) && (
                      <MenuItem key="create-tenant-button" value="create">
                        <Grid container alignItems="center">
                          <Grid item>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '8px',
                              }}
                            >
                              Create Tenant <AddIcon />
                            </Typography>
                          </Grid>
                        </Grid>
                      </MenuItem>
                    ),
                ]}
              />
            </Box>
          ) : (
            <Typography
              sx={{
                display: 'inline',
                color: 'white',
                fontSize: '1.5rem',
                marginRight: '12px',
              }}
            >
              {allTenants && !!allTenants.length && allTenants.length === 1
                ? allTenants[0].displayName
                : companyName}
            </Typography>
          )}

          <IconButton
            aria-label="User guide"
            color="inherit"
            size="large"
            onClick={handleHelpIconClick}
          >
            <HelpIcon fontSize="large" />
          </IconButton>

          <IconButton aria-label="Notifications" color="inherit" size="large">
            <NotificationsActiveIcon fontSize="large" />
          </IconButton>

          <IconButton
            aria-label="Jobs"
            color="inherit"
            size="large"
            onClick={() => dispatch(openJobs())}
            ref={jobsAnchorRef}
            title="Jobs"
          >
            <Badge
              badgeContent={
                jobs?.filter((j) => ['pending', 'processing'].includes(j.status)).length
              }
              color="primary"
            >
              <PlaylistAddCheckIcon fontSize="large" />
            </Badge>
          </IconButton>

          <Button
            startIcon={<AccountCircleIcon sx={{ margin: '0 4px 0 16px' }} />}
            endIcon={<ExpandMoreIcon />}
            sx={{ padding: 0, color: 'white', textTransform: 'capitalize' }}
            onClick={handleProfileClick}
          >
            {userName}
          </Button>
        </Flex.H>
      </Flex.H>

      {Boolean(anchorProfile) && (
        <Menu
          id="appbar-profile"
          anchorEl={anchorProfile}
          keepMounted
          open={Boolean(anchorProfile)}
          onClose={handleProfileClose}
        >
          {user?.permissions?.has(ACCOUNT_ADMIN_PERMISSION) && (
            <Link component={RouterLink} to={ACCOUNT_ROUTE} underline="none">
              <MenuItem
                selected={pathname === '/bbweb/account'}
                sx={{ color: '#000' }}
                onClick={handleProfileClose}
              >
                Account
              </MenuItem>
            </Link>
          )}

          <MenuItem onClick={handleLogout} sx={{ color: '#000' }}>
            Sign Out
          </MenuItem>
        </Menu>
      )}

      {Boolean(anchorMenu) && hasMenuItems && (
        <Menu
          id="appbar-menu"
          anchorEl={anchorMenu}
          keepMounted
          open={Boolean(anchorMenu)}
          onClick={handleMenuClose}
        >
          {renderMenuItems}
        </Menu>
      )}

      <AsyncJobsDialog anchorEl={jobsAnchorRef.current} />
    </>
  );
};

AppBarFull.propTypes = {
  pathname: PropTypes.string.isRequired,
  navList: PropTypes.array.isRequired,
  companyName: PropTypes.string,
  userName: PropTypes.string.isRequired,
};

export default AppBarFull;
